import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, Folder, ChevronRight, Clock, LayoutGrid, Sparkles } from 'lucide-react'

import { isMiniappFolder, structuralFolderKind, isChainPointerRow } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { FileIcon, fileIconProps, miniappIconProps, useScreenShown, useSwapScroll } from '@papan/core-ui/ui'
import { filesApi } from '@papan/files/client'
import { fileKeys, identityGuardedRead, useFilesList, useRecentWorkPreview } from '@papan/files/client/queries'
import { useDebounced } from './hooks'
import { ProjectCard } from './ProjectCard'
import { useProjectCardActions, type FolderPicker } from './useProjectCardActions'
import { suggestProjects, useProjects, useRecentProjects, type ProjectItem } from './data'
import { RecentGroupList } from './RecentGroupRow'
import { useRecentNav, useRecentWords } from './RecentView'

const LIST_OPTS = { sortBy: 'name' as const, sortDir: 'asc' as const, limit: 500 }

/**
 * The drive Home page — the Google-Drive-style landing: search, "jump back
 * in" project suggestions, Library quick access, and "Jump back in": the
 * viewer's own recent work, grouped (#653 — the same data as the Recent page).
 * Pure composition over existing caches (folder lists, search) plus the two
 * project endpoints and `/api/files/recent-work`.
 */
export function DriveHomeView({ urlPrefix, isOrg, orgEmail, userName, tenantName, onRequestMiniappView, FolderPickerDialog }: {
  urlPrefix: string
  isOrg?: boolean
  orgEmail?: string
  userName?: string
  tenantName?: string
  /** Threaded from App.tsx (owns @papan/collab): select the editor face before opening. */
  onRequestMiniappView?: (folderId: string, view: 'run' | 'settings') => void
  /** Destination folder picker for Move/Copy — injected to avoid a files → workspace edge. */
  FolderPickerDialog?: FolderPicker
}) {
  const { t } = useTranslation('files')
  const navigate = useNavigate()

  // ─── Search ─────────────────────────────────────────────────────────
  const [query, setQuery] = useState('')
  const debounced = useDebounced(query.trim(), 250)
  const searchOpen = debounced.length >= 2
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const [searchFocused, setSearchFocused] = useState(false)
  const queryClient = useQueryClient()
  const searchKey = fileKeys.search(null, debounced)
  const search = useQuery({
    queryKey: searchKey,
    // #1210: identity-scoped key — never read as, or answered by, another identity
    queryFn: () => identityGuardedRead(queryClient, searchKey, () => filesApi.searchFiles(debounced, { limit: 12 })),
    enabled: searchOpen,
    staleTime: 30_000,
  })

  // Close the results sheet on outside click
  useEffect(() => {
    if (!searchFocused) return
    const onDown = (e: MouseEvent) => {
      if (!searchBoxRef.current?.contains(e.target as Node)) setSearchFocused(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [searchFocused])

  // ─── Projects ───────────────────────────────────────────────────────
  const projects = useProjects()
  const recentProjects = useRecentProjects()
  const suggested = useMemo(
    () => suggestProjects(recentProjects.data ?? [], projects.data ?? [], 12),
    [recentProjects.data, projects.data],
  )
  // Cap the suggested strip at 2 rows: measure the grid's actual column count
  // and show columns × 2 cards (recomputed on resize).
  const suggestGridRef = useRef<HTMLDivElement>(null)
  const [suggestCols, setSuggestCols] = useState(4)
  useEffect(() => {
    const el = suggestGridRef.current
    if (!el) return
    const measure = () => {
      const cols = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length
      if (cols > 0) setSuggestCols(cols)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [suggested.length])
  const suggestedShown = suggested.slice(0, suggestCols * 2)

  // ─── Library quick access ───────────────────────────────────────────
  // New hierarchy tenants have a root `Library` folder; legacy tenants
  // (pre-Stage-4 backfill) don't — fall back to plain root folders.
  const root = useFilesList(null, LIST_OPTS)
  const libraryFolder = root.data?.files.find(f => f.isFolder && f.name === 'Library')
  const library = useFilesList(libraryFolder?.id ?? null, LIST_OPTS, { enabled: !!libraryFolder })
  const quickFolders = useMemo(() => {
    const rows = libraryFolder ? (library.data?.files ?? []) : (root.data?.files ?? [])
    return rows
      .filter(f => f.isFolder && !f.name.startsWith('.') && !f.name.startsWith('_'))
      .slice(0, 8)
  }, [libraryFolder, library.data, root.data])

  // ─── Jump back in (#653) ────────────────────────────────────────────
  // The viewer's OWN recent work (#653 decision 1); the team's is the Recent page's default.
  // Paused while the Drive deck is hidden (only marked stale); reloads when shown.
  const recent = useRecentWorkPreview('mine', { subscribed: useScreenShown() })
  const recentWords = useRecentWords(orgEmail, tenantName)
  const recentNav = useRecentNav(urlPrefix)

  // Mixed strip: a mini-app opens as an app window, a project as a workspace.
  const openProject = (p: ProjectItem) => navigate(`${urlPrefix}/${isMiniappFolder(p.name) ? 'app' : 'workspace'}/${p.folderId}`)
  const browseProject = (p: ProjectItem) => navigate(`${urlPrefix}/folder/${p.folderId}`)
  const editSettings = (p: ProjectItem) => { onRequestMiniappView?.(p.folderId, 'settings'); navigate(`${urlPrefix}/workspace/${p.folderId}`) }
  // Same rich context-menu actions + dialogs as the Projects page.
  const { handlers: cardActions, dialogs: cardDialogs } = useProjectCardActions({ isOrg, FolderPickerDialog })
  const cardHandlers = { onOpen: openProject, onBrowse: browseProject, onEditSettings: editSettings, ...cardActions }

  // #897 — keeps its place across a phone/desktop layout swap.
  const scrollRef = useRef<HTMLDivElement>(null)
  useSwapScroll('drive-home', scrollRef)

  const hour = new Date().getHours()
  const greetingKey = hour < 12 ? 'driveHome.greeting.morning' : hour < 18 ? 'driveHome.greeting.afternoon' : 'driveHome.greeting.evening'

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto w-full px-8 py-8">
        {/* ─── Greeting + search ─── */}
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} className="text-indigo-400" />
          <h1 className="text-xl font-semibold themed-text">
            {/* No name → drop the trailing comma (incl. the CJK 、，variants) */}
            {t(greetingKey, { name: userName || '' }).trim().replace(/[,，]$/, '')}
          </h1>
        </div>
        {tenantName && <p className="text-sm themed-text-muted mb-5">{t('driveHome.subtitle', { tenant: tenantName })}</p>}

        <div ref={searchBoxRef} className="relative mb-8 max-w-xl">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
          <input
            name="drive-home-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onKeyDown={(e) => {
              // Enter → the dedicated results page (Google-Drive style);
              // the dropdown stays for quick jumps.
              if (e.key === 'Enter' && query.trim().length >= 2) {
                navigate(`${urlPrefix}/search?q=${encodeURIComponent(query.trim())}`)
              }
              if (e.key === 'Escape') setSearchFocused(false)
            }}
            placeholder={t('driveHome.search.placeholder')}
            data-testid="drive-home-search"
            className="w-full themed-input ws-drive-pill rounded-xl pl-10 pr-4 py-2.5 text-sm"
          />
          {/* NOT .ws-popover — that class is position:fixed (built for
              body-portaled toolbar popovers) and would anchor this to the
              viewport, pushing it below the fold. Plain absolute panel,
              same surface recipe as DriveView's row menus. */}
          {searchFocused && searchOpen && (
            // A passing overlay, not page content: a layout swap never keeps a
            // place in it (#897).
            <div data-scroll-skip className="absolute top-full left-0 right-0 mt-2 themed-bg-tertiary border themed-border-secondary rounded-xl py-1.5 z-20 max-h-80 overflow-y-auto shadow-xl">
              {search.isLoading ? (
                <div className="px-4 py-3 text-sm themed-text-muted">{t('driveHome.loading')}</div>
              ) : (search.data?.files.length ?? 0) === 0 ? (
                <div className="px-4 py-3 text-sm themed-text-muted">{t('driveHome.search.noResults')}</div>
              ) : (
                <>
                  {search.data!.files.slice(0, 8).map(f => (
                    <button
                      key={f.id}
                      data-scroll-anchor={f.id}
                      onClick={() => {
                        setSearchFocused(false)
                        setQuery('')
                        // A chain is REVEALED beside its siblings, never entered from search: search rows don't carry
                        // `chainNeedsApproval`, and the row's own open runs the D17 gate (I114).
                        if (f.isFolder && !isChainPointerRow(f)) navigate(`${urlPrefix}/folder/${f.id}`)
                        else navigate(f.parentId ? `${urlPrefix}/folder/${f.parentId}?reveal=${f.id}` : `${urlPrefix}/folder?reveal=${f.id}`)
                      }}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm themed-text hover:themed-bg-secondary text-left"
                    >
                      <FileIcon {...fileIconProps(f)} size={15} {...miniappIconProps(f)} />
                      <span className="truncate">{f.name}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => navigate(`${urlPrefix}/search?q=${encodeURIComponent(debounced)}`)}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm border-t themed-border-secondary themed-text-secondary hover:themed-text hover:themed-bg-secondary text-left"
                    data-testid="drive-home-search-all"
                  >
                    <Search size={14} className="shrink-0" />
                    {t('driveHome.search.seeAll', { query: debounced })}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ─── Suggested projects ─── */}
        {suggested.length > 0 && (
          <section className="mb-9">
            <div className="flex items-center justify-between mb-3">
              <h2 className="ws-home-heading">
                <LayoutGrid size={14} />
                {t('driveHome.sections.suggestedProjects')}
              </h2>
              <button
                onClick={() => navigate(`${urlPrefix}/projects`)}
                className="text-xs themed-text-muted hover:themed-text flex items-center gap-0.5 transition-colors"
              >
                {t('driveHome.sections.viewAll')}
                <ChevronRight size={13} />
              </button>
            </div>
            <div ref={suggestGridRef} className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-3">
              {suggestedShown.map(p => (
                <ProjectCard
                  key={p.folderId}
                  project={p}
                  // A real `.miniapp` renders as an app; anything else (incl. a
                  // `.miniapp.project` workspace shell) renders as a plain
                  // project. isMiniappFolder is strict — false for `.project`.
                  variant={isMiniappFolder(p.name) ? 'miniapp' : 'project'}
                  dateMode="modified"
                  {...cardHandlers}
                />
              ))}
            </div>
          </section>
        )}

        {/* ─── Library quick access ─── */}
        {quickFolders.length > 0 && (
          <section className="mb-9">
            <h2 className="ws-home-heading mb-3">
              <Folder size={14} />
              {libraryFolder ? t('driveHome.sections.library') : t('driveHome.sections.folders')}
            </h2>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2.5">
              {quickFolders.map(f => (
                <button
                  key={f.id}
                  data-scroll-anchor={f.id}
                  // A chain is revealed beside its siblings (its row runs the D17 gate), never entered here.
                  onClick={() => navigate(isChainPointerRow(f)
                    ? (f.parentId ? `${urlPrefix}/folder/${f.parentId}?reveal=${f.id}` : `${urlPrefix}/folder?reveal=${f.id}`)
                    : `${urlPrefix}/folder/${f.id}`)}
                  className="ws-home-tile group"
                  data-testid="home-library-tile"
                >
                  <FileIcon {...fileIconProps(f)} size={16} structural={structuralFolderKind(libraryFolder ? [libraryFolder.name, f.name] : [f.name])} {...miniappIconProps(f)} />
                  <span className="text-sm themed-text truncate">{f.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ─── Jump back in (#653) ─── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="ws-home-heading">
              <Clock size={14} />
              {t('driveHome.sections.jumpBackIn')}
            </h2>
            <button
              onClick={() => navigate(`${urlPrefix}/recent`)}
              className="text-xs themed-text-muted hover:themed-text flex items-center gap-0.5 transition-colors"
              data-testid="home-recent-view-all"
            >
              {t('driveHome.sections.viewAll')}
              <ChevronRight size={13} />
            </button>
          </div>
          {recent.isLoading ? (
            <div className="text-sm themed-text-muted px-1 py-2">{t('driveHome.loading')}</div>
          ) : (recent.data?.groups.length ?? 0) === 0 ? (
            <div className="text-sm themed-text-muted px-1 py-2" data-testid="home-recent-empty">{t('driveHome.recent.empty.home')}</div>
          ) : (
            <RecentGroupList groups={recent.data!.groups.slice(0, 6)} words={recentWords} nav={recentNav} />
          )}
        </section>
      </div>
      {/* Project-card context-menu dialogs (share / access / rename / picker / details / debug). */}
      {cardDialogs}
    </div>
  )
}
